import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bell, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const LEVELS = { info: "text-sky-400", success: "text-emerald-400", warning: "text-amber-400", danger: "text-rose-400" };

export default function Notifications() {
  const { t } = useLang();
  const { user } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", level: "info" });

  const load = async () => { const { data } = await api.get("/notifications"); setNotifs(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/notifications", form); toast.success("Notification sent"); setOpen(false); setForm({ title: "", body: "", level: "info" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };

  const canCreate = user?.role === "super_admin" || user?.role === "admin";

  return (
    <DashboardLayout
      title={t("notif.title")}
      subtitle={t("notif.subtitle")}
      right={canCreate && (
        <button data-testid="notif-new" onClick={() => setOpen(true)} className="btn-scale h-9 px-3 rounded-md bg-white text-zinc-950 font-medium text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("notif.new")}
        </button>
      )}
    >
      <div className="space-y-3" data-testid="notif-list">
        {notifs.map((n) => (
          <div key={n.notification_id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex items-start gap-4">
            <Bell className={`w-4 h-4 mt-1 ${LEVELS[n.level] || "text-zinc-400"}`} />
            <div className="flex-1">
              <div className="text-sm text-zinc-100 font-medium">{n.title}</div>
              <div className="text-sm text-zinc-400 mt-0.5">{n.body}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
            {!n.read && (
              <button data-testid={`notif-read-${n.notification_id}`} onClick={() => markRead(n.notification_id)} className="btn-scale text-xs text-zinc-400 hover:text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> mark read
              </button>
            )}
          </div>
        ))}
        {notifs.length === 0 && <div className="text-zinc-500 text-sm p-8 text-center border border-dashed border-zinc-800 rounded-xl">{t("common.no_data")}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="notif-dialog">
          <DialogHeader><DialogTitle>{t("notif.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input data-testid="notif-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            <textarea data-testid="notif-body" placeholder="Body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            <select data-testid="notif-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
              {Object.keys(LEVELS).map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="notif-save" onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-white text-zinc-950 text-sm font-medium">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
